import { useId } from 'react'

interface TimePickerProps {
  value: string // "H:MM AM/PM" format or empty string
  onChange: (value: string) => void
  label?: string
  error?: string
  disabled?: boolean
}

function parseTimeValue(value: string): { hour: string; minute: string; period: string } {
  if (!value) return { hour: '', minute: '', period: 'AM' }
  const match = value.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i)
  if (!match) return { hour: '', minute: '', period: 'AM' }
  return { hour: match[1], minute: match[2], period: match[3].toUpperCase() }
}

function formatTimeValue(hour: string, minute: string, period: string): string {
  if (!hour || !minute) return ''
  return `${hour}:${minute} ${period}`
}

export function TimePicker({ value, onChange, label, error, disabled = false }: TimePickerProps) {
  const id = useId()
  const { hour, minute, period } = parseTimeValue(value)

  const handleChange = (newHour: string, newMinute: string, newPeriod: string) => {
    onChange(formatTimeValue(newHour, newMinute, newPeriod))
  }

  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  }

  const labelStyles: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  }

  const rowStyles: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  }

  const selectWrapperStyles: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  }

  const selectStyles: React.CSSProperties = {
    width: '100%',
    padding: '0.75rem 2rem 0.75rem 0.75rem',
    fontSize: '16px',
    border: `1px solid ${error ? '#EF4444' : '#d1d5db'}`,
    borderRadius: '8px',
    backgroundColor: disabled ? '#f3f4f6' : 'white',
    color: '#1a1a1a',
    cursor: disabled ? 'not-allowed' : 'pointer',
    appearance: 'none',
    outline: 'none',
    minHeight: '44px',
    fontFamily: 'inherit',
  }

  const iconStyles: React.CSSProperties = {
    position: 'absolute',
    right: '0.5rem',
    pointerEvents: 'none',
    color: '#6b7280',
  }

  const periodButtonStyles = (isActive: boolean): React.CSSProperties => ({
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: isActive ? 600 : 400,
    border: `1px solid ${error ? '#EF4444' : isActive ? '#2563eb' : '#d1d5db'}`,
    borderRadius: '8px',
    backgroundColor: isActive ? '#2563eb' : 'white',
    color: isActive ? 'white' : '#374151',
    cursor: disabled ? 'not-allowed' : 'pointer',
    padding: '0 0.75rem',
    fontFamily: 'inherit',
  })

  const errorStyles: React.CSSProperties = {
    fontSize: '13px',
    color: '#EF4444',
    marginTop: '0.25rem',
  }

  const chevronIcon = (
    <svg style={iconStyles} width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  )

  const hours = Array.from({ length: 12 }, (_, i) => {
    const h = i + 1
    return { value: String(h), label: String(h) }
  })

  const minutes = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(
    m => ({ value: m, label: m })
  )

  return (
    <div style={containerStyles}>
      {label && (
        <label htmlFor={`${id}-hour`} style={labelStyles}>
          {label}
        </label>
      )}
      <div style={rowStyles}>
        {/* Hour */}
        <div style={selectWrapperStyles}>
          <select
            id={`${id}-hour`}
            value={hour}
            onChange={e => handleChange(e.target.value, minute || '00', period)}
            disabled={disabled}
            style={selectStyles}
            aria-label="Hour"
          >
            <option value="" disabled>
              --
            </option>
            {hours.map(h => (
              <option key={h.value} value={h.value}>
                {h.label}
              </option>
            ))}
          </select>
          {chevronIcon}
        </div>

        <span style={{ fontSize: '20px', fontWeight: 600, color: '#374151' }}>:</span>

        {/* Minute */}
        <div style={selectWrapperStyles}>
          <select
            id={`${id}-minute`}
            value={minute}
            onChange={e => handleChange(hour || '12', e.target.value, period)}
            disabled={disabled}
            style={selectStyles}
            aria-label="Minute"
          >
            <option value="" disabled>
              --
            </option>
            {minutes.map(m => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          {chevronIcon}
        </div>

        {/* AM/PM toggle */}
        <button
          type="button"
          style={periodButtonStyles(period === 'AM')}
          onClick={() => handleChange(hour, minute, 'AM')}
          disabled={disabled}
          aria-label="AM"
        >
          AM
        </button>
        <button
          type="button"
          style={periodButtonStyles(period === 'PM')}
          onClick={() => handleChange(hour, minute, 'PM')}
          disabled={disabled}
          aria-label="PM"
        >
          PM
        </button>
      </div>
      {error && (
        <span style={errorStyles} role="alert">
          {error}
        </span>
      )}
    </div>
  )
}
